// Iteration 081 -- Rugby League Table page (renamed 098).
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { RugbyLeagueTableRow } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function RugbyLeagueTablePage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const [rows, setRows] = useState<RugbyLeagueTableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    api
      .getRugbyLeagueTable(schoolId)
      .then((r: import("@dugads/shared").RugbyLeagueTableResponse) => setRows(r.rows))
      .catch((e: unknown) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [schoolId]);

  function exportCsv() {
    const header = ["Team", "School", "P", "W", "D", "L", "PF", "PA", "Diff", "TB", "LB", "Pts"].join(",");
    const body = rows
      .map((r) =>
        [
          r.team_display_name,
          r.school_display_name,
          r.played,
          r.won,
          r.drawn,
          r.lost,
          r.points_for,
          r.points_against,
          r.points_diff,
          r.try_bonuses,
          r.losing_bonuses,
          r.league_points,
        ].join(","),
      )
      .join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rugby-league-table-${schoolId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <p data-page="rugby-league-table" data-state="loading">Loading…</p>;

  return (
    <div className="flex flex-col gap-6" data-page="rugby-league-table">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Rugby League Table</h1>
        {rows.length > 0 && (
          <button className="text-sm underline" onClick={exportCsv} data-action="export-csv">
            Export CSV
          </button>
        )}
      </div>
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Teams</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No teams yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team</TableHead>
                  <TableHead className="text-right">P</TableHead>
                  <TableHead className="text-right">W</TableHead>
                  <TableHead className="text-right">D</TableHead>
                  <TableHead className="text-right">L</TableHead>
                  <TableHead className="text-right">PF</TableHead>
                  <TableHead className="text-right">PA</TableHead>
                  <TableHead className="text-right">+/-</TableHead>
                  <TableHead className="text-right">TB</TableHead>
                  <TableHead className="text-right">LB</TableHead>
                  <TableHead className="text-right font-semibold">Pts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.team_id}>
                    <TableCell>{r.team_display_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.played}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.won}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.drawn}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.lost}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.points_for}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.points_against}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.points_diff}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.try_bonuses}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.losing_bonuses}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{r.league_points}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
